import "leaflet-pulse-icon/src/L.Icon.Pulse";

import * as L from "leaflet";
import PropTypes from "prop-types";
import React, { useContext } from "react";
import { Map, Marker, Polygon, TileLayer, Tooltip, Polyline } from "react-leaflet";
import { get, min, max, unzip } from "lodash";
import { useTheme } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import { StringUtils } from "../../../util";

import { leafletURL } from "../../../services/map.service";
import { FleetContext } from "../../../context";
import { VEHICLE_POINT_OVERLAP_AREA } from "../../../constants";
import buildAssetsUrl from "../../../util/strings/buildAssetsUrl";
import VehiclePin from "../../../assets/vehicle-pin.svg";

export const VehicleHistoryMapContainer = ({
  mapHeight,
  vehiclePosition,
  visibleEvents,
  mapBoundsPadding,
}) => {
  const { geofences } = useContext(FleetContext);

  const vehicleLat = get(vehiclePosition, "coordinates[1]");
  const vehicleLng = get(vehiclePosition, "coordinates[0]");

  const vehicleHasCoords = vehicleLat && vehicleLng;
  const theme = useTheme();
  const { t } = useTranslation();

  const getToolTip = (name, properties) => {
    const { label, incentive } = properties;
    const tip = (
      <span>
        {name && (
          <span>
            {name}
            <br />
          </span>
        )}
        {label}
        {incentive?.amount && (
          <span>
            <br />
            {StringUtils.formatNumber(incentive.amount, incentive?.currency)}
          </span>
        )}
      </span>
    );
    return <Tooltip opacity={0.75}>{tip}</Tooltip>;
  };

  if (!vehicleHasCoords) return null;

  const locatedEvents = visibleEvents.filter((event) => event.location?.coordinates);
  const latLngPairs = locatedEvents.map(
    ({
      location: {
        coordinates: [lng, lat],
      },
    }) => [lat, lng],
  );
  const [lats, longs] = unzip(latLngPairs);

  // Frame the selected history events when there are any, otherwise the current position
  const hasEventCoords = latLngPairs.length > 0;
  const minLat = hasEventCoords ? min(lats) : vehicleLat;
  const minLng = hasEventCoords ? min(longs) : vehicleLng;
  const maxLat = hasEventCoords ? max(lats) : vehicleLat;
  const maxLng = hasEventCoords ? max(longs) : vehicleLng;

  const bounds = L.latLngBounds([
    [minLat - mapBoundsPadding, minLng - mapBoundsPadding],
    [maxLat + mapBoundsPadding, maxLng + mapBoundsPadding],
  ]);

  const getPathIcon = (isStartingPos, isEndingPos) => {
    // A single selected event isn't a path, so mark it rather than using trip start/end icons
    if (latLngPairs.length === 1) return L.divIcon({ className: "vehicle-location history-location" });
    const baseURL = buildAssetsUrl("VehicleHistoryMap/icons");
    if (isEndingPos) {
      return L.icon({
        iconUrl: `${baseURL}/tripEnd.svg`,
        iconSize: [24, 24],
        iconAnchor: [-2, 30],
      });
    }
    if (isStartingPos) {
      return L.icon({
        iconUrl: `${baseURL}/tripStart.svg`,
        iconSize: [24, 24],
        iconAnchor: [10, 22],
      });
    }
    return L.divIcon({ className: "vehicle-location" });
  };
  let vehicleOverlapsPointZone = false;

  return (
    <Map
      bounds={bounds}
      doubleClickZoom
      scrollWheelZoom={false}
      dragging
      keyboard
      animate
      touchZoom
      style={{ height: mapHeight }}
    >
      <TileLayer
        url={leafletURL}
        attribution={`&copy; Superpedestrian ${new Date().getFullYear()}`}
      />

      {geofences &&
        geofences.map((g) => {
          let fenceProperties = null;
          if (g.is_operational_zone) {
            fenceProperties = {
              color: theme.palette.geofencesTypeColors.is_operational_zone,
              label: t("Fleet.Map.Geofences.is_operational_zone"),
            };
          } else if (g.rules.length) {
            // rules array has been filtered for active rules,
            // all rules on this geofence will have the same label and parking incentive
            fenceProperties = {
              color: theme.palette.geofencesTypeColors[g.rules[0].label],
              label: t(`Fleet.Map.Geofences.${g.rules[0].label}`),
              incentive: g.rules[0]?.parking_incentive,
            };
          }
          if (fenceProperties) {
            const isPointPolygon = g.polygon.geometry.type === "Point";
            const position = isPointPolygon ? [g.coordinates] : g.coordinates;
            const weight = isPointPolygon ? 8 : 2;
            if (isPointPolygon) {
              const [gLat, gLng] = position[0];
              const latDiff = Math.abs(gLat - vehicleLat);
              const lngDiff = Math.abs(gLng - vehicleLng);
              const radiusFromVehicle = Math.sqrt(latDiff ** 2 + lngDiff ** 2);

              if (radiusFromVehicle < VEHICLE_POINT_OVERLAP_AREA) {
                vehicleOverlapsPointZone = true;
              }
            }
            return (
              <Polygon
                key={g.id}
                interactive
                positions={position}
                color={fenceProperties.color}
                weight={weight}
                fill={!g.is_operational_zone}
              >
                {getToolTip(g.name, fenceProperties)}
              </Polygon>
            );
          }
          return null;
        })}
      {vehicleOverlapsPointZone ? (
        <Marker
          position={[vehicleLat, vehicleLng]}
          icon={L.icon({
            iconUrl: VehiclePin,
            iconSize: [40, 40],
            iconAnchor: [20, 40],
            className: "vehicle-overlaps-zone",
          })}
        />
      ) : (
        <Marker
          position={[vehicleLat, vehicleLng]}
          icon={L.divIcon({
            className: "vehicle-location vehicle-pulse",
          })}
        />
      )}
      {hasEventCoords && <Polyline positions={latLngPairs} />}
      {latLngPairs.map(([lat, lng], idx) => {
        const isStartingPos = idx === latLngPairs.length - 1;
        const isEndingPos = idx === 0;
        return (
          <Marker
            key={locatedEvents[idx].key}
            position={[lat, lng]}
            icon={getPathIcon(isStartingPos, isEndingPos)}
          />
        );
      })}
    </Map>
  );
};

VehicleHistoryMapContainer.propTypes = {
  mapHeight: PropTypes.number,
  visibleEvents: PropTypes.arrayOf(PropTypes.object),
  vehiclePosition: PropTypes.object.isRequired,
  mapBoundsPadding: PropTypes.number,
};

VehicleHistoryMapContainer.defaultProps = {
  mapHeight: 650,
  visibleEvents: [],
  mapBoundsPadding: 0.0015,
};

export default VehicleHistoryMapContainer;
